import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { STATUS } from '../../utils/status';
import Error from '../error/error';
import { useStyles } from '../../assets/styles/listTableStyle';
import { getAdjudicationConfigAnalytics, getConfigAnalytics, getConfigAnalyticsStages, getStageConfigOverview } from '../../redux/slices/analytics/configAnalytics';
import { useParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { getConfig, getConfigForTable } from '../../redux/slices/config';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import api from '../../utils/api';

const PROJECT_CONFIG_ANALYTICS_TAB = 'project-config-analytics';
const CONFIG_PAGE_SIZE = 10;
const OVERVIEW_PAGE_SIZE = 10;

const ORDERING_OPTIONS = [
  { value: 'name', label: 'Name (A-Z)' },
  { value: '-name', label: 'Name (Z-A)' },
  { value: 'points', label: 'Points (Low-High)' },
  { value: '-points', label: 'Points (High-Low)' },
  { value: 'order', label: 'Order (Low-High)' },
  { value: '-order', label: 'Order (High-Low)' },
  { value: 'adjudication_points_earned', label: 'Adj. Points Earned' },
  { value: 'scoring_mode', label: 'Scoring Mode' },
  { value: 'enable_scoring', label: 'Enable Scoring' },
  { value: 'created_at', label: 'Created At (Oldest)' },
  { value: '-updated_at', label: 'Updated At (Newest)' },
];

const ConfigAnalytics = () => {
  const { adjudicationConfigAnalytics, getAdjudicationConfigAnalyticsStatus, availableStages, getAvailableStagesStatus, error } = useSelector(
    (state: RootState) => state.configAnalytics
  );

  const config = useSelector(
    (state: RootState) => state.config.config
  );

  const getConfigStatus = useSelector(
    (state: RootState) => state.config.getConfigStatus
  );

  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();

  const [selectedTab, setSelectedTab] = useState<string>('');
  const [collectionIds, setCollectionIds] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [ordering, setOrdering] = useState('-updated_at');
  const [typeFilter, setTypeFilter] = useState('TASK');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [configAnalyticsById, setConfigAnalyticsById] = useState<Record<string, any[]>>({});
  const [configAnalyticsStatusById, setConfigAnalyticsStatusById] = useState<Record<string, STATUS>>({});
  const [overviewData, setOverviewData] = useState<any[]>([]);
  const [overviewSummary, setOverviewSummary] = useState<any>(null);
  const [overviewCount, setOverviewCount] = useState<number>(0);
  const [overviewStatus, setOverviewStatus] = useState<STATUS>(STATUS.NOT_STARTED);
  const [overviewExpanded, setOverviewExpanded] = useState<boolean>(false);
  const [overviewSearchInput, setOverviewSearchInput] = useState<string>('');
  const [overviewSearch, setOverviewSearch] = useState<string>('');
  const [overviewScoreCategory, setOverviewScoreCategory] = useState<string>('');
  const [overviewPage, setOverviewPage] = useState<number>(1);
  const [overviewFetching, setOverviewFetching] = useState<boolean>(false);
  const [overviewExporting, setOverviewExporting] = useState<boolean>(false);
  const [overviewExportError, setOverviewExportError] = useState<boolean>(false);

  const tableHeaders = [
    "IDENTIFIER",
    "POINTS EARNED",
    "TOTAL POINTS",
    "AGREEMENT RATE",
  ];

  const headers = [
    "OPTION VALUE",
    "PROJECT LIMIT",
    "USER LIMIT",
    "TOTAL USED",
    "REMAINING",
    "UNIQUE USERS",
    "USER HIT LIMIT",
    "STATUS"
  ];

  const selectedStage = availableStages?.find((stage: any) => String(stage.stage_id) === selectedTab);
  const showAdjudicationAnalytics = selectedStage?.is_adjudication === true;
  const showStageConfigAnalytics = selectedStage?.is_adjudication === false;
  const stageConfigResults = showStageConfigAnalytics ? config?.results || [] : [];
  const selectedStageLabel = selectedStage?.stage_name || 'Stage Config Analytics';
  const hasManualScoring = overviewData.some((row: any) => row.scoring_mode === 'MANUAL');

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: React.SetStateAction<string> | null
  ) => {
    if (newValue !== null) {
      setSelectedTab(newValue as string);
    }
  };

  useEffect(() => {
    dispatch(
      getConfigAnalyticsStages({
        project_id: id || '',
      })
    );
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(
      getConfig({
        category: "TASK"
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (config?.data?.length) {
      const nextCollectionIds = config.data
        .filter((item: any) => ["single_select", "multi_select"].includes(item.value))
        .map((item: any) => item.id)
        .join(',');

      setCollectionIds(nextCollectionIds);
    }
  }, [config?.data]);

  useEffect(() => {
    if (!showStageConfigAnalytics || !collectionIds) return;
    dispatch(
      getConfigForTable({
        project_id: id || '',
        stage_id: selectedTab,
        collection_ids: collectionIds,
        search: searchQuery || undefined,
        type: typeFilter,
        ordering,
        page: currentPage,
        pageSize: CONFIG_PAGE_SIZE,
        paginate: true,
      })
    );
  }, [dispatch, showStageConfigAnalytics, id, selectedTab, collectionIds, searchQuery, typeFilter, ordering, currentPage]);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 300);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [searchInput]);

  useEffect(() => {
    if (availableStages && availableStages.length > 0 && !selectedTab) {
      setSelectedTab(String(availableStages[0].stage_id));
    }
  }, [availableStages, selectedTab]);

  useEffect(() => {
    // Reset all stage-specific state and auto-open the overview accordion on stage change
    setCurrentPage(1);
    setSearchInput('');
    setSearchQuery('');
    setConfigAnalyticsById({});
    setConfigAnalyticsStatusById({});
    setOverviewData([]);
    setOverviewSummary(null);
    setOverviewCount(0);
    setOverviewStatus(STATUS.NOT_STARTED);
    setOverviewExpanded(true);
    setOverviewSearchInput('');
    setOverviewSearch('');
    setOverviewScoreCategory('');
    setOverviewPage(1);
  }, [selectedTab]);

  const handleOverviewAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
    setOverviewExpanded(expanded);
  };

  useEffect(() => {
    if (!selectedTab) return;
    if (overviewStatus === STATUS.SUCESS || overviewStatus === STATUS.FAILED) {
      setOverviewFetching(true);
    } else {
      setOverviewStatus(STATUS.PENDING);
    }
    dispatch(
      getStageConfigOverview({
        project_id: id || '',
        stage_id: selectedTab,
        search: overviewSearch || undefined,
        score_category: overviewScoreCategory || undefined,
        page: overviewPage,
        page_size: OVERVIEW_PAGE_SIZE,
      })
    ).then((action: any) => {
      if (getStageConfigOverview.fulfilled.match(action)) {
        setOverviewData(action.payload?.data?.results || []);
        setOverviewSummary(action.payload?.data?.summary || null);
        setOverviewCount(action.payload?.data?.count || 0);
        setOverviewStatus(STATUS.SUCESS);
      } else {
        setOverviewStatus(STATUS.FAILED);
      }
      setOverviewFetching(false);
    });
  }, [dispatch, selectedTab, id, overviewSearch, overviewScoreCategory, overviewPage]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setOverviewSearch(overviewSearchInput.trim());
      setOverviewPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [overviewSearchInput]);

  const handleOverviewExport = async () => {
    setOverviewExporting(true);
    setOverviewExportError(false);
    try {
      const params = new URLSearchParams({
        project_id: id || '',
        ...(selectedTab && { stage_id: selectedTab }),
        ...(overviewScoreCategory && { score_category: overviewScoreCategory }),
      });
        const response = await api.get(
          `project/qa-analytics/config-analytics/export/?${params.toString()}`
        );
        const downloadUrl = response.data?.url;

        if (typeof downloadUrl !== 'string' || !downloadUrl) {
          throw 'Export URL missing from response';
        }

        const fileResponse = await fetch(downloadUrl);

        if (!fileResponse.ok) {
          throw 'Failed to fetch exported file';
        }

        const blob = await fileResponse.blob();
        const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
        link.setAttribute('download', downloadUrl.split('/').pop() || `config-analytics-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setOverviewExporting(false);
    } catch {
      setOverviewExporting(false);
      setOverviewExportError(true);
    }
  };

  const handleAccordionChange =
    (configId: string) =>
      (_event: React.SyntheticEvent, expanded: boolean) => {

        if (expanded) {
          setConfigAnalyticsStatusById((prev) => ({
            ...prev,
            [configId]: STATUS.PENDING,
          }));

          dispatch(
            getConfigAnalytics({
              config_id: configId,
            })
          ).then((action: any) => {
            if (getConfigAnalytics.fulfilled.match(action)) {
              setConfigAnalyticsById((prev) => ({
                ...prev,
                [configId]: action.payload?.data || [],
              }));
              setConfigAnalyticsStatusById((prev) => ({
                ...prev,
                [configId]: STATUS.SUCESS,
              }));
              return;
            }

            setConfigAnalyticsStatusById((prev) => ({
              ...prev,
              [configId]: STATUS.FAILED,
            }));
          });
        }
      };


  useEffect(() => {
    if (showAdjudicationAnalytics) {
      dispatch(
        getAdjudicationConfigAnalytics({
          project_id: id || '',
        })
      );
    }
  }, [dispatch, id, showAdjudicationAnalytics]);

  const configCount = Number((config as any)?.count ?? stageConfigResults.length ?? 0);
  const totalPages = Math.max(1, Math.ceil(configCount / CONFIG_PAGE_SIZE));

  const overviewAccordionContent = (
    <>
      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 2.5 }}>
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>ACCEPTED (AS-IS)</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#111827' }}>{overviewSummary?.as_is_count ?? 0}</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#22C55E' }}>{overviewSummary?.as_is_pct ?? 0}%</Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>of {overviewSummary?.total_reviewed_tasks ?? 0} evaluations</Typography>
          <Box sx={{ mt: 1, height: '4px', borderRadius: '2px', backgroundColor: '#D1FAE5' }}>
            <Box sx={{ height: '4px', borderRadius: '2px', backgroundColor: '#22C55E', width: `${Math.min(overviewSummary?.as_is_pct ?? 0, 100)}%`, transition: 'width 0.3s ease' }} />
          </Box>
        </Box>
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>ACCEPTED (WITH EDITS)</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#111827' }}>{overviewSummary?.with_edits_count ?? 0}</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#3B82F6' }}>{overviewSummary?.with_edits_pct ?? 0}%</Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>of {overviewSummary?.total_reviewed_tasks ?? 0} evaluations</Typography>
          <Box sx={{ mt: 1, height: '4px', borderRadius: '2px', backgroundColor: '#DBEAFE' }}>
            <Box sx={{ height: '4px', borderRadius: '2px', backgroundColor: '#3B82F6', width: `${Math.min(overviewSummary?.with_edits_pct ?? 0, 100)}%`, transition: 'width 0.3s ease' }} />
          </Box>
        </Box>
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>REJECTED</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#111827' }}>{overviewSummary?.rejected_count ?? 0}</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#EF4444' }}>{overviewSummary?.rejected_pct ?? 0}%</Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>of {overviewSummary?.total_reviewed_tasks ?? 0} evaluations</Typography>
          <Box sx={{ mt: 1, height: '4px', borderRadius: '2px', backgroundColor: '#FEE2E2' }}>
            <Box sx={{ height: '4px', borderRadius: '2px', backgroundColor: '#EF4444', width: `${Math.min(overviewSummary?.rejected_pct ?? 0, 100)}%`, transition: 'width 0.3s ease' }} />
          </Box>
        </Box>
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>RE-WORK</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#111827' }}>{overviewSummary?.rework_count ?? 0}</Typography>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#F97316' }}>{overviewSummary?.rework_pct ?? 0}%</Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>of {overviewSummary?.total_reviewed_tasks ?? 0} evaluations</Typography>
          <Box sx={{ mt: 1, height: '4px', borderRadius: '2px', backgroundColor: '#FED7AA' }}>
            <Box sx={{ height: '4px', borderRadius: '2px', backgroundColor: '#F97316', width: `${Math.min(overviewSummary?.rework_pct ?? 0, 100)}%`, transition: 'width 0.3s ease' }} />
          </Box>
        </Box>
        <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, backgroundColor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>OVERALL ACCEPTANCE RATE</Typography>
          <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#7C3AED' }}>{overviewSummary?.overall_acceptance_rate_pct ?? 0}%</Typography>
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>of {overviewSummary?.total_reviewed_tasks ?? 0} evaluations</Typography>
          <Box sx={{ mt: 1, height: '4px', borderRadius: '2px', backgroundColor: '#EDE9FE' }}>
            <Box sx={{ height: '4px', borderRadius: '2px', backgroundColor: '#7C3AED', width: `${Math.min(overviewSummary?.overall_acceptance_rate_pct ?? 0, 100)}%`, transition: 'width 0.3s ease' }} />
          </Box>
        </Box>
      </Box>

      {/* Filters + Export */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'center', flexWrap: 'wrap', backgroundColor: '#F8FAFC', borderRadius: '12px', p: 1.5, border: '1px solid #E2E8F0' }}>
        <TextField
          size="small"
          placeholder="Search config name..."
          value={overviewSearchInput}
          onChange={(e) => setOverviewSearchInput(e.target.value)}
          sx={{
            minWidth: 230,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#fff',
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#03BBD2' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03BBD2', borderWidth: '1.5px' },
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
          }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '17px', color: '#94A3B8' }} /></InputAdornment> }}
        />
        <TextField
          size="small"
          select
          value={overviewScoreCategory}
          onChange={(e) => { setOverviewScoreCategory(e.target.value); setOverviewPage(1); }}
          sx={{
            minWidth: 170,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: '#fff',
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#03BBD2' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03BBD2', borderWidth: '1.5px' },
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
            '& .MuiSelect-select': { color: overviewScoreCategory ? '#111827' : '#94A3B8' },
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (val: any) => {
              if (val === 'AUTO') return 'Auto';
              if (val === 'MANUAL') return 'Manual';
              return 'All Score Category';
            },
          }}
        >
          <MenuItem value="">All Score Category</MenuItem>
          <MenuItem value="AUTO">Auto</MenuItem>
          <MenuItem value="MANUAL">Manual</MenuItem>
        </TextField>
        {(overviewSearchInput || overviewScoreCategory) && (
          <Button
            size="small"
            onClick={() => { setOverviewSearchInput(''); setOverviewScoreCategory(''); setOverviewPage(1); }}
            sx={{ textTransform: 'none', color: '#64748B', fontFamily: 'Poppins', fontSize: '12px', fontWeight: 500, borderRadius: '8px', px: 1.5, '&:hover': { backgroundColor: '#F1F5F9', color: '#EF4444' } }}
          >
            ✕ Clear
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          onClick={handleOverviewExport}
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '16px !important' }} />}
          sx={{ textTransform: 'none', fontFamily: 'Poppins', fontSize: '12px', fontWeight: 600, borderRadius: '8px', px: 2, backgroundColor: '#03BBD2', boxShadow: 'none', '&:hover': { backgroundColor: '#029DB7', boxShadow: 'none' } }}
        >
          Export
        </Button>
      </Box>

      {/* Table section */}
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#374151', letterSpacing: '0.5px', mb: 1.5, textTransform: 'uppercase' }}>
        Config Performance Summary
      </Typography>

      {overviewStatus === STATUS.PENDING ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : overviewStatus === STATUS.FAILED ? (
        <Error message="Failed to load overview data." />
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
          {overviewFetching && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.55)', borderRadius: '8px' }}>
              <CircularProgress size={24} sx={{ color: '#03BBD2' }} />
            </Box>
          )}
          <TableContainer sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden', opacity: overviewFetching ? 0.45 : 1, transition: 'opacity 0.2s ease' }}>
            <Table sx={{ minWidth: 900 }} aria-label="overview config performance table">
              <TableHead sx={{ backgroundColor: '#F7F9FA' }}>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', verticalAlign: 'middle', borderRight: '1px solid #E5E7EB' }}>Config Name</TableCell>
                  <TableCell rowSpan={2} sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', verticalAlign: 'middle', borderRight: '1px solid #E5E7EB' }}>Score Mode</TableCell>
                  <TableCell rowSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', verticalAlign: 'middle', borderRight: '1px solid #E5E7EB' }}>Total Evaluations</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', borderRight: '1px solid #E5E7EB' }}>Accepted (As-Is)</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', borderRight: '1px solid #E5E7EB' }}>Accepted (With Edits)</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', borderRight: '1px solid #E5E7EB' }}>Rejected</TableCell>
                  <TableCell colSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', borderRight: '1px solid #E5E7EB' }}>Re-work</TableCell>
                  <TableCell rowSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', verticalAlign: 'middle', borderRight: '1px solid #E5E7EB' }}>Acceptance Rate %</TableCell>
                  <TableCell rowSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', verticalAlign: 'middle', borderRight: '1px solid #E5E7EB' }}>Most Common Error Type</TableCell>
                  <TableCell rowSpan={2} align="center" sx={{ fontSize: '11px', fontWeight: 700, fontFamily: 'Poppins', color: '#374151', verticalAlign: 'middle' }}>Top Rejected Reasons</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>Count</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>%</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>Count</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>%</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>Count</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>%</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280', borderRight: '1px solid #E5E7EB' }}>Count</TableCell>
                  <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#6B7280' }}>%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overviewData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 4 }}>
                      <Error message="No overview data available for the selected filters." />
                    </TableCell>
                  </TableRow>
                ) : (
                  overviewData.map((row: any, index: number) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#F9FAFB' }, '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ fontSize: '12px', fontFamily: 'Poppins', fontWeight: 500, borderRight: '1px solid #E5E7EB' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#E0F2FE', color: '#0284C7', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>
                            {String(row.config_name || row.identifier || '?')[0].toUpperCase()}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{row.config_name}</span>
                            {row.identifier && (
                              <span style={{ fontSize: '10px', color: '#9CA3AF', fontWeight: 400, marginTop: '1px' }}>{row.identifier}</span>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', fontFamily: 'Poppins', borderRight: '1px solid #E5E7EB' }}>{row.score_category ? row.score_category.replace(/\s*\(.*?\)/, '') : '-'}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', fontWeight: 600, borderRight: '1px solid #E5E7EB' }}>{row.total_evaluations ?? 0}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#22C55E', fontWeight: 600 }}>{row.as_is_count ?? 0}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#22C55E', borderRight: '1px solid #E5E7EB' }}>{row.as_is_pct ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#3B82F6', fontWeight: 600 }}>{row.with_edits_count ?? 0}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#3B82F6', borderRight: '1px solid #E5E7EB' }}>{row.with_edits_pct ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#EF4444', fontWeight: 600 }}>{row.rejected_count ?? 0}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#EF4444', borderRight: '1px solid #E5E7EB' }}>{row.rejected_pct ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#F97316', fontWeight: 600 }}>{row.rework_count ?? 0}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', color: '#F97316', borderRight: '1px solid #E5E7EB' }}>{row.rework_pct ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', fontWeight: 700, color: '#7C3AED', borderRight: '1px solid #E5E7EB' }}>{row.acceptance_rate_pct ?? 0}%</TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins', borderRight: '1px solid #E5E7EB' }}>
                        {row.most_common_error_type && row.most_common_error_type !== '-' ? (
                          <Box component="span" sx={{ backgroundColor: '#F3F4F6', borderRadius: '12px', px: 1.5, py: 0.5, fontSize: '11px', color: '#374151', whiteSpace: 'nowrap' }}>
                            {row.most_common_error_type}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '12px', fontFamily: 'Poppins' }}>
                        {row.top_rejected_reason && row.top_rejected_reason !== '-' ? (
                          <Box component="span" sx={{ backgroundColor: '#FEF2F2', borderRadius: '12px', px: 1.5, py: 0.5, fontSize: '11px', color: '#B91C1C', whiteSpace: 'nowrap' }}>
                            {row.top_rejected_reason}
                          </Box>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {overviewCount > OVERVIEW_PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Pagination
                count={Math.max(1, Math.ceil(overviewCount / OVERVIEW_PAGE_SIZE))}
                page={overviewPage}
                onChange={(_e, page) => setOverviewPage(page)}
                color="primary"
                shape="rounded"
                size="small"
              />
            </Box>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <TableContainer sx={{ mt: 2 }}>
      <div
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid lightgrey',
          borderRadius: '15px',
          padding: '5px',
          marginTop: "1px",
          display: 'inline-flex',
          marginBottom: "10px"
        }}
      >

        <ToggleButtonGroup
          color="standard"
          value={selectedTab}
          exclusive
          onChange={handleTabChange}
        >

          {/* Adjudication Config Overview tab — kept for future use
          <ToggleButton
            value={PROJECT_CONFIG_ANALYTICS_TAB}
            sx={{
              border: 'none !important',
              fontFamily: 'Montserrat !important',
              fontSize: '14px !important',
              fontWeight: '600 !important',
              lineHeight: '24px !important',
              color: selectedTab === PROJECT_CONFIG_ANALYTICS_TAB ? '#fff !important' : '#6C6C6C !important',
              borderRadius: '15px !important',
              backgroundColor: selectedTab === PROJECT_CONFIG_ANALYTICS_TAB ? '#03BBD2 !important' : '#fff !important',
              textTransform: 'none !important',
            }}
          >
            Adjudication Config Overview
          </ToggleButton>
          */}

          {getAvailableStagesStatus === STATUS.SUCESS &&
            availableStages.map((stage: any) => (

              <ToggleButton
                key={stage.stage_id}
                value={String(stage.stage_id)}
                sx={{
                  border: 'none !important',
                  fontFamily: 'Montserrat !important',
                  fontSize: '14px !important',
                  fontWeight: '600 !important',
                  lineHeight: '24px !important',
                  color: selectedTab === String(stage.stage_id) ? '#fff !important' : '#6C6C6C !important',
                  borderRadius: '15px !important',
                  backgroundColor: selectedTab === String(stage.stage_id) ? '#03BBD2 !important' : '#fff !important',
                  textTransform: 'none !important',
                }}
              >
                {stage.stage_name}
              </ToggleButton>

            ))}

        </ToggleButtonGroup>
      </div>

      </TableContainer>

      {showAdjudicationAnalytics && (
        <>

          <TableContainer sx={{ mt: 2 }}>
            <Box
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '16px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                padding: '8px'
              }}
            >
              {/* Overview accordion */}
              <Accordion
                onChange={handleOverviewAccordionChange}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  backgroundColor: '#FCFDFD',
                  marginBottom: '10px',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="adj-overview-content"
                  id="adj-overview-header"
                  sx={{
                    flexDirection: "row-reverse",
                    minHeight: '56px',
                    padding: '0 14px',
                    backgroundColor: '#F8FAFC',
                    "& .MuiAccordionSummary-expandIconWrapper": { marginRight: "8px", color: '#03BBD2' },
                    '& .MuiAccordionSummary-content': { margin: '12px 0' }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontFamily: 'Poppins', color: '#111827' }}>
                    Overview
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '16px', backgroundColor: '#FAFAFA' }}>
                  {overviewAccordionContent}
                </AccordionDetails>
              </Accordion>

              {/* Adjudication Config Overview accordion */}
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  backgroundColor: '#FCFDFD',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="adjudication-config-content"
                  id="adjudication-config-header"
                  sx={{
                    flexDirection: "row-reverse",
                    minHeight: '56px',
                    padding: '0 14px',
                    backgroundColor: '#F8FAFC',
                    "& .MuiAccordionSummary-expandIconWrapper": { marginRight: "8px", color: '#03BBD2' },
                    '& .MuiAccordionSummary-content': { margin: '12px 0' }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontFamily: 'Poppins', color: '#111827' }}>
                    Adjudication Config Overview
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '0 16px 16px' }}>
                  <Table sx={{ minWidth: 650 }} aria-label="adjudication config analytics table">
                    <TableHead sx={{ backgroundColor: '#F7F9FA', '&:first-child td, &:first-child th': { borderBottom: 0 } }}>
                      <TableRow>
                        {tableHeaders.map((header) => (
                          <TableCell
                            align="left"
                            sx={{ fontSize: '10px', fontWeight: 600, lineHeight: '14px', fontFamily: 'Poppins' }}
                            key={header}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getAdjudicationConfigAnalyticsStatus === STATUS.PENDING ? (
                        <TableRow>
                          <TableCell style={{ textAlign: 'center' }} colSpan={4}>
                            <CircularProgress size={20} />
                          </TableCell>
                        </TableRow>
                      ) : getAdjudicationConfigAnalyticsStatus === STATUS.SUCESS && adjudicationConfigAnalytics && adjudicationConfigAnalytics.length > 0 ? (
                        <>
                          {adjudicationConfigAnalytics.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell align="left" sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}>
                                {row.identifier}
                              </TableCell>
                              <TableCell align="left" sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}>
                                {row.adjudication_points_earned ?? 'N/A'}
                              </TableCell>
                              <TableCell align="left" sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}>
                                {row.adjudication_points_total ?? 'N/A'}
                              </TableCell>
                              <TableCell align="left" sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}>
                                {row.adjudication_agreement_rate !== null ? `${row.adjudication_agreement_rate}%` : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ) : getAdjudicationConfigAnalyticsStatus === STATUS.SUCESS && adjudicationConfigAnalytics && adjudicationConfigAnalytics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className={classes.noUsersMessage}>
                            <Error message="Analytics will be displayed once limits are configured for the project." />
                          </TableCell>
                        </TableRow>
                      ) : getAdjudicationConfigAnalyticsStatus === STATUS.FAILED ? (
                        <TableRow>
                          <TableCell colSpan={4} className={classes.noUsersMessage}>
                            <Error message="No data to display, Adjudication Config overview applies only to adjudication projects." />
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell style={{ textAlign: 'center' }} colSpan={4}>
                            <CircularProgress size={20} />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </Box>
          </TableContainer>
        </>
      )}

      {showStageConfigAnalytics && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, mb: 1 }}>
            <Tooltip title="Filter" placement="bottom-end" arrow>
              <IconButton
                aria-label="filter options"
                onClick={(event) => setFilterAnchorEl(event.currentTarget)}
                size="small"
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  color: 'white',
                  backgroundColor: '#3D85C6',
                  '&:hover': { backgroundColor: '#2F6EA8' },
                }}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>Filters</Typography>

              <TextField
                size="small"
                label="Search"
                placeholder="Name or identifier"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />

              <TextField
                size="small"
                select
                label="Type"
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <MenuItem value="TASK">TASK</MenuItem>
                <MenuItem value="REVIEW">REVIEW</MenuItem>
              </TextField>

              <TextField
                size="small"
                select
                label="Ordering"
                value={ordering}
                onChange={(event) => {
                  setOrdering(event.target.value);
                  setCurrentPage(1);
                }}
              >
                {ORDERING_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                    setTypeFilter('TASK');
                    setOrdering('-updated_at');
                    setCurrentPage(1);
                    setFilterAnchorEl(null);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
                <Button variant="contained" size="small" onClick={() => setFilterAnchorEl(null)} sx={{ textTransform: 'none' }}>
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>

          <TableContainer sx={{ mt: 2 }}>
            <Box
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '16px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
                padding: '8px'
              }}
            >
            <Accordion
              onChange={handleOverviewAccordionChange}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '12px !important',
                overflow: 'hidden',
                backgroundColor: '#FCFDFD',
                marginBottom: '10px',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="overview-analytics-content"
                id="overview-analytics-header"
                sx={{
                  flexDirection: "row-reverse",
                  minHeight: '56px',
                  padding: '0 14px',
                  backgroundColor: '#F8FAFC',
                  "& .MuiAccordionSummary-expandIconWrapper": { marginRight: "8px", color: '#03BBD2' },
                  '& .MuiAccordionSummary-content': { margin: '12px 0' }
                }}
              >
                <Typography sx={{ fontWeight: 600, fontFamily: 'Poppins', color: '#111827' }}>
                  Overview
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '16px', backgroundColor: '#FAFAFA' }}>
                {overviewAccordionContent}
              </AccordionDetails>
            </Accordion>

            {getConfigStatus === STATUS.PENDING ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                <CircularProgress size={20} />
              </Box>
            ) : stageConfigResults.length === 0 ? null : stageConfigResults.map((item: any) => {
              const itemConfigAnalytics = configAnalyticsById[item.id] || [];
              const itemConfigAnalyticsStatus = configAnalyticsStatusById[item.id] || STATUS.NOT_STARTED;

              return (

              <Accordion
                key={item.id}
                onChange={handleAccordionChange(item.id)}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  backgroundColor: '#FCFDFD',
                  marginBottom: '10px',
                  '&:before': {
                    display: 'none'
                  },
                  '&:last-child': {
                    marginBottom: 0
                  }
                }}
              >

                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="config-analytics-content"
                  id="config-analytics-header"
                  sx={{
                    flexDirection: "row-reverse",
                    minHeight: '56px',
                    padding: '0 14px',
                    backgroundColor: '#F8FAFC',
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginRight: "8px",
                      color: '#03BBD2'
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0'
                    }
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontFamily: 'Poppins', color: '#111827' }}>
                    {item.config_details?.name} - {item.identifier}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: '0 16px 16px' }}>

                  <Table sx={{ minWidth: 650 }} aria-label="config analytics table">

                    <TableHead
                      sx={{
                        backgroundColor: '#F7F9FA',
                        '&:first-child td, &:first-child th': { borderBottom: 0 }
                      }}
                    >

                      <TableRow>

                        {headers.map((header) => (
                          <TableCell
                            key={header}
                            align="left"
                            sx={{
                              fontSize: '10px',
                              fontWeight: 600,
                              lineHeight: '14px',
                              fontFamily: 'Poppins'
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}

                      </TableRow>

                    </TableHead>

                    <TableBody>

                      {itemConfigAnalyticsStatus === STATUS.PENDING ? (

                        <TableRow>
                          <TableCell style={{ textAlign: 'center' }} colSpan={4}>
                            <CircularProgress size={20} />
                          </TableCell>
                        </TableRow>

                      ) : itemConfigAnalyticsStatus === STATUS.SUCESS &&
                        itemConfigAnalytics.length > 0 ? (

                        itemConfigAnalytics.map((row: any, index: number) => (

                          <TableRow key={index}>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.option_value}
                            </TableCell>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.project_limit ?? 'N/A'}
                            </TableCell>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.user_limit ?? 'N/A'}
                            </TableCell>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.total_used ?? 'N/A'}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.remaining}
                            </TableCell>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.unique_users ?? 'N/A'}
                            </TableCell>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.users_hit_limit ?? 'N/A'}
                            </TableCell>

                            <TableCell
                              align="left"
                              sx={{ fontSize: '12px', fontWeight: 400, fontFamily: 'Poppins' }}
                            >
                              {row.status ?? 'N/A'}
                            </TableCell>

                          </TableRow>

                        ))

                      ) : itemConfigAnalyticsStatus === STATUS.SUCESS &&
                        itemConfigAnalytics.length === 0 ? (

                        <TableRow>
                          <TableCell colSpan={4} className={classes.noUsersMessage}>
                            <Error message="There is no data added as of yet." />
                          </TableCell>
                        </TableRow>

                      ) : itemConfigAnalyticsStatus === STATUS.FAILED ? (

                        <TableRow>
                          <TableCell colSpan={4} className={classes.noUsersMessage}>
                            <Error message="Failed to load config analytics data." />
                          </TableCell>
                        </TableRow>

                      ) : (

                        <TableRow>
                          <TableCell style={{ textAlign: 'center' }} colSpan={4}>
                            <CircularProgress size={20} />
                          </TableCell>
                        </TableRow>

                      )}

                    </TableBody>

                  </Table>

                </AccordionDetails>

              </Accordion>

              );
            })}
            </Box>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_event, page) => setCurrentPage(page)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </>
      )}

      {/* Export loading / error modal */}
      <Dialog
        open={overviewExporting || overviewExportError}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          {overviewExporting && (
            <>
              <CircularProgress size={52} sx={{ color: '#03BBD2', mb: 2.5 }} />
              <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '16px', color: '#111827', mb: 1 }}>
                Preparing Export...
              </Typography>
              <Typography sx={{ fontFamily: 'Poppins', fontSize: '13px', color: '#6B7280' }}>
                Your Excel file is being generated. This may take a few seconds.
              </Typography>
            </>
          )}
          {overviewExportError && (
            <>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2.5 }}>
                <Typography sx={{ fontSize: '22px', color: '#EF4444', lineHeight: 1 }}>✕</Typography>
              </Box>
              <Typography sx={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '16px', color: '#111827', mb: 1 }}>
                Export Failed
              </Typography>
              <Typography sx={{ fontFamily: 'Poppins', fontSize: '13px', color: '#6B7280', mb: 3 }}>
                Something went wrong while generating the file. Please try again.
              </Typography>
              <Button
                variant="contained"
                onClick={() => setOverviewExportError(false)}
                sx={{ textTransform: 'none', fontFamily: 'Poppins', fontWeight: 600, borderRadius: '8px', px: 3, backgroundColor: '#03BBD2', boxShadow: 'none', '&:hover': { backgroundColor: '#029DB7', boxShadow: 'none' } }}
              >
                Close
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConfigAnalytics;